import { type ComponentPropsWithoutRef, forwardRef, useId } from "react";
import { Icon, type IconComponent } from "../Icon";
import styles from "./Input.module.css";

/**
 * Input — labeled single-line text input.
 *
 * Wraps a native `<input>` with:
 *   - A visible or visually-hidden label (label is REQUIRED for a11y)
 *   - Optional leading icon
 *   - Optional help text or error message (error takes precedence)
 *   - Invalid / disabled states
 *
 * The component owns the label ↔ input association via `useId()`:
 * if you pass an explicit `id` it's respected, otherwise a unique
 * React ID is generated and both `htmlFor` and the input's `id` are
 * wired to it automatically. No consumer needs to think about
 * htmlFor matching.
 *
 * Uses `forwardRef` so consumers can imperatively focus the input
 * (e.g. pairing code field, search box) via a ref.
 *
 * Token references:
 *   - container bg        → --bg-primary
 *   - border              → --stroke-neutral (focused: --brand, invalid: --text-negative)
 *   - placeholder         → --text-tertiary
 *   - label               → --text-primary
 *   - help text           → --text-tertiary
 *   - error text          → --text-negative
 */

type Size = "xs" | "sm" | "md";

export type InputProps = {
  /** Required accessible label. Set `hideLabel` to render it visually
   *  hidden (still reachable for screen readers). */
  label: string;
  /** Visually hide the label (e.g. for a search bar where the placeholder
   *  and leading icon carry the affordance). */
  hideLabel?: boolean;
  /** Input size. Defaults to `md`. */
  size?: Size;
  /** Optional icon rendered on the left inside the input row. */
  leadingIcon?: IconComponent;
  /** Marks the field as invalid — swaps border/focus ring to negative. */
  invalid?: boolean;
  /** Help text shown beneath the input. Hidden when `errorMessage` is set. */
  helpText?: string;
  /** Error message shown beneath the input. Implies `invalid`. */
  errorMessage?: string;
} & Omit<ComponentPropsWithoutRef<"input">, "size" | "className">;

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  {
    label,
    hideLabel,
    size = "md",
    leadingIcon,
    invalid,
    helpText,
    errorMessage,
    id,
    disabled,
    ...rest
  },
  ref
) {
  const autoId = useId();
  const inputId = id ?? autoId;
  const helpId = `${inputId}-help`;
  const errorId = `${inputId}-error`;
  // errorMessage takes precedence over helpText (one visible at a time).
  const showError = Boolean(errorMessage);
  const showHelp = !showError && Boolean(helpText);
  const isInvalid = invalid || showError;

  const wrapperClasses = [
    styles.field,
    styles[`size-${size}`],
    isInvalid ? styles.invalid : null,
    disabled ? styles.disabled : null,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div className={wrapperClasses}>
      <label htmlFor={inputId} className={hideLabel ? styles.labelVisuallyHidden : styles.label}>
        {label}
      </label>
      <div className={styles.inputRow}>
        {leadingIcon ? (
          <span className={styles.leadingIcon}>
            <Icon as={leadingIcon} size={size === "md" ? "md" : "sm"} tone="inherit" />
          </span>
        ) : null}
        <input
          ref={ref}
          id={inputId}
          className={styles.input}
          disabled={disabled}
          aria-invalid={isInvalid || undefined}
          aria-describedby={showError ? errorId : showHelp ? helpId : undefined}
          {...rest}
        />
      </div>
      {showHelp ? (
        <span id={helpId} className={styles.help}>
          {helpText}
        </span>
      ) : null}
      {showError ? (
        <span id={errorId} className={styles.error} role="alert">
          {errorMessage}
        </span>
      ) : null}
    </div>
  );
});
